/**
 * Thời gian. Luật: lưu UTC (timestamptz), nghiệp vụ tính ở Asia/Ho_Chi_Minh.
 * Giờ làm việc và ca lưu dạng "phút từ nửa đêm" (0..1440).
 * Xem CLAUDE.md mục 3 và ADR-009.
 */
import { DateTime } from 'luxon'

/** Múi giờ nghiệp vụ duy nhất của dự án. Việt Nam không có giờ mùa hè. */
export const VN_ZONE = 'Asia/Ho_Chi_Minh'

/** Số phút trong một ngày. */
export const MINUTES_PER_DAY = 1440

/** Thời điểm hiện tại theo giờ Việt Nam. Truyền `now` vào hàm nghiệp vụ để test được. */
export function nowVn(): DateTime {
  return DateTime.now().setZone(VN_ZONE)
}

/** Đọc một ngày dạng "2026-10-02" theo giờ Việt Nam (đầu ngày). */
export function parseVnDate(isoDate: string): DateTime {
  const dt = DateTime.fromISO(isoDate, { zone: VN_ZONE })
  if (!dt.isValid) throw new Error(`Ngay khong hop le: "${isoDate}" (${dt.invalidReason})`)
  return dt.startOf('day')
}

/**
 * `"YYYY-MM-DD"` có phải một ngày CÓ THẬT không (31/02, tháng 13, 0000-01-01 → false). Không ném lỗi.
 * Regex `^\d{4}-\d{2}-\d{2}$` một mình cho qua "2026-02-30", rồi tầng dưới ném lỗi thường -> 500 (QA 02/10).
 * Dùng trong `.refine()` của zod ở biên API.
 */
export function laNgayVnThat(s: string): boolean {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(s)) return false
  const dt = DateTime.fromISO(s, { zone: VN_ZONE })
  return dt.isValid && dt.year >= 1900
}

/** Ghép ngày (giờ VN) với "phút từ nửa đêm" thành một thời điểm cụ thể. */
export function vnDateAtMinute(isoDate: string, minuteOfDay: number): DateTime {
  assertMinuteOfDay(minuteOfDay)
  return parseVnDate(isoDate).plus({ minutes: minuteOfDay })
}

/** Lấy "phút từ nửa đêm" của một thời điểm, tính theo giờ Việt Nam. */
export function toMinuteOfDay(dt: DateTime): number {
  const vn = dt.setZone(VN_ZONE)
  return vn.hour * 60 + vn.minute
}

/** Ngày (dạng 2026-10-02) theo giờ Việt Nam của một thời điểm. */
/**
 * Ngày theo giờ Việt Nam của một thời điểm, dạng `"2026-10-05"`.
 *
 * Nhận `Date` vì các route tiêm đồng hồ vào bằng `now(): Date` cho test. Đi qua đây chứ không
 * tự `new Date().toISOString().slice(0, 10)`: chuỗi đó là ngày **UTC**, nên từ 00:00 tới 07:00
 * giờ Việt Nam nó trả về ngày HÔM QUA — bảy tiếng mỗi ngày sai một cách rất khó thấy.
 */
export function ngayVnCua(d: Date): string {
  return toVnDateKey(DateTime.fromJSDate(d))
}

export function toVnDateKey(dt: DateTime): string {
  const iso = dt.setZone(VN_ZONE).toISODate()
  if (iso === null) throw new Error('Khong lay duoc ngay tu thoi diem khong hop le')
  return iso
}

/**
 * Thứ trong tuần theo **ISO**: 1 = Thứ Hai ... 7 = Chủ Nhật.
 *
 * Vì sao ISO chứ không phải 0 = Chủ Nhật: cột `weekday` trong cơ sở dữ liệu dùng ISO
 * (`EXTRACT(ISODOW FROM ngay)` của Postgres trả đúng dải này), và `weekday` của Luxon cũng vậy.
 * Một quy ước, không phép chuyển đổi nào ở giữa — chỗ nào có hai quy ước thì sớm muộn có lỗi
 * lệch một ngày, và loại lỗi đó rất khó thấy vì nó chỉ sai vào Chủ Nhật.
 */
export function toWeekday(dt: DateTime): number {
  return dt.setZone(VN_ZONE).weekday
}

/** Thứ ISO (1..7) của một ngày dạng `"2026-10-04"`. */
export function isoWeekdayOf(isoDate: string): number {
  return parseVnDate(isoDate).weekday
}

/**
 * Tuần thứ mấy kể từ một mốc Thứ Hai cố định. Dùng để xoay vòng mẫu ca nhiều tuần
 * (`week_index = weekOfEpoch(ngay) % soTuanTrongMau`).
 *
 * Không dùng số tuần ISO trong năm vì nó nhảy ở ranh giới năm (tuần 52 sang tuần 1) — vòng xoay
 * 2 hoặc 3 tuần sẽ lệch một tuần vào mỗi dịp Tết dương lịch, mà chẳng ai nghĩ tới chỗ đó.
 */
export function weekOfEpoch(isoDate: string): number {
  const moc = parseVnDate('2020-01-06') // một Thứ Hai
  const soNgay = parseVnDate(isoDate).diff(moc, 'days').days
  return Math.floor(soNgay / 7)
}

/** Cộng/trừ ngày trên chuỗi ngày: `themNgayVn('2026-10-05', -1)` -> `'2026-10-04'`. */
export function themNgayVn(isoDate: string, soNgay: number): string {
  return toVnDateKey(parseVnDate(isoDate).plus({ days: soNgay }))
}

/** Dãy `soNgay` ngày liên tiếp bắt đầu từ `tuNgay` (bao gồm chính nó). */
export function eachVnDate(tuNgay: string, soNgay: number): string[] {
  if (!Number.isInteger(soNgay) || soNgay < 0) {
    throw new Error(`soNgay phai la so nguyen khong am: ${soNgay}`)
  }
  const bd = parseVnDate(tuNgay)
  const ra: string[] = []
  for (let i = 0; i < soNgay; i++) ra.push(toVnDateKey(bd.plus({ days: i })))
  return ra
}

/** Tên thứ tiếng Việt để hiển thị. Nhận số ISO 1..7. */
export function weekdayNameVn(weekday: number): string {
  const ten = ['Thứ Hai', 'Thứ Ba', 'Thứ Tư', 'Thứ Năm', 'Thứ Sáu', 'Thứ Bảy', 'Chủ Nhật']
  const t = ten[weekday - 1]
  if (t === undefined) throw new Error(`Thu phai trong khoang 1..7, nhan: ${weekday}`)
  return t
}

/** Chuỗi ISO có offset, ví dụ "2026-10-02T09:00:00+07:00". Đây là dạng trả về cho client. */
export function toIsoWithOffset(dt: DateTime): string {
  const iso = dt.setZone(VN_ZONE).toISO({ suppressMilliseconds: true })
  if (iso === null) throw new Error('Thoi diem khong hop le, khong the tao ISO')
  return iso
}

/** Như `toIsoWithOffset` nhưng nhận `Date` (thời điểm đọc từ DB) — để route không phải import Luxon. */
export function isoVnCua(d: Date): string {
  return toIsoWithOffset(DateTime.fromJSDate(d))
}

/** Đọc chuỗi ISO **bắt buộc có offset hoặc Z**. Chuỗi thiếu offset là lỗi dữ liệu. */
export function parseIsoStrict(iso: string): DateTime {
  if (!/(Z|[+-]\d{2}:\d{2})$/.test(iso.trim())) {
    throw new Error(`Chuoi thoi gian phai co offset hoac Z: "${iso}"`)
  }
  const dt = DateTime.fromISO(iso, { setZone: true })
  if (!dt.isValid) throw new Error(`Thoi gian khong hop le: "${iso}" (${dt.invalidReason})`)
  return dt
}

/** Hiển thị giờ 24h cho UI: "14:30". */
export function formatVnTime(dt: DateTime): string {
  return dt.setZone(VN_ZONE).toFormat('HH:mm')
}

/** Hiển thị ngày cho UI: "02/10/2026". */
export function formatVnDate(dt: DateTime): string {
  return dt.setZone(VN_ZONE).toFormat('dd/MM/yyyy')
}

/**
 * Hiển thị một mốc giờ trong ngày cho UI: `540` -> `"09:00"`.
 *
 * Có hàm riêng vì giao diện rất hay cần đúng việc này (lưới khe giờ, bảng ca) mà **không** có
 * ngày đi kèm. Trước đây mỗi ứng dụng tự viết một bản — hai bản sao của cùng một phép tính là
 * hai chỗ phải sửa khi luật đổi, và chắc chắn có ngày quên một chỗ.
 */
export function formatMinuteOfDay(minutes: number): string {
  if (!Number.isInteger(minutes) || minutes < 0 || minutes > MINUTES_PER_DAY) {
    throw new Error(`Phut trong ngay phai trong 0..${MINUTES_PER_DAY}, nhan: ${minutes}`)
  }
  const h = Math.floor(minutes / 60)
  const m = minutes % 60
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`
}

/** Ngày đầy đủ cho chỗ cần chắc chắn: `"2026-09-21"` -> `"Thứ Hai, 21/09/2026"`. */
export function formatVnDateFull(isoDate: string): string {
  const dt = parseVnDate(isoDate)
  return `${weekdayNameVn(toWeekday(dt))}, ${formatVnDate(dt)}`
}

/** Ngày rút gọn cho danh sách: `"2026-09-21"` -> `"Thứ Hai, 21/09"`. */
export function formatVnDateShort(isoDate: string): string {
  const dt = parseVnDate(isoDate)
  return `${weekdayNameVn(toWeekday(dt))}, ${dt.toFormat('dd/MM')}`
}

/** Hiển thị thời lượng cho UI: 45 -> "45 phút", 90 -> "1 giờ 30 phút". */
export function formatDuration(minutes: number): string {
  if (!Number.isInteger(minutes) || minutes < 0) {
    throw new Error(`Thoi luong phai la so nguyen khong am, nhan: ${minutes}`)
  }
  const hours = Math.floor(minutes / 60)
  const mins = minutes % 60
  if (hours === 0) return `${mins} phút`
  if (mins === 0) return `${hours} giờ`
  return `${hours} giờ ${mins} phút`
}

function assertMinuteOfDay(minuteOfDay: number): void {
  if (!Number.isInteger(minuteOfDay) || minuteOfDay < 0 || minuteOfDay > MINUTES_PER_DAY) {
    throw new Error(`Phut trong ngay phai la so nguyen 0..1440, nhan: ${minuteOfDay}`)
  }
}
